import React, { useEffect, useState, useContext } from 'react'
import IAddress from '../../server/src/Interfaces/IAddress'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCircleCheck, faLocationDot, faPlus, faSpinner, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import IFireplace from '../../server/src/Interfaces/IFireplace'
import ky from 'ky'
import IAddressCategory from '../../server/src/Interfaces/IAddressCategory'
import IAddressType from '../../server/src/Interfaces/IAddressType'
import toast from 'react-hot-toast'
import { GlobalContext } from '../App'
import { updateFireplaces, updateAddressTypes, updateAddressCategories } from '../Modules/Data'
import { API_URL } from '../Globals'
import { AppPermission, hasPermission } from '../Permissions/PermissionDefinitions'

const AddCustomer: React.FC = () => {
    const { globalVariable, setGlobalVariable } = useContext(GlobalContext)
    const [newAddressForm, setNewAddressForm] = useState<IAddress>({
        _id: '',
        firstname: '',
        lastname: '',
        address: '',
        zip: '',
        city: '',
        lastSweepDate: '',
        nextSweepDate: '',
        phoneNumbers: [],
        additionalInfo: '',
        selectedFireplaceIds: [],
        addressType: 0,
        addressCategoryId: 1
    })
    const [fireplaceIds, setFireplaceIds] = useState<IFireplace[]>([])
    const [addressCategories, setAddressCategories] = useState<IAddressCategory[]>([])
    const [addressTypes, setAddressTypes] = useState<IAddressType[]>([])
    const [existingAddresses, setExistingAddresses] = useState<IAddress[]>([])
    const [isCheckingAddress, setIsCheckingAddress] = useState(false)
    const [addressCheckFailed, setAddressCheckFailed] = useState(false)
    const canEditAddresses = hasPermission(globalVariable.permissions, AppPermission.ADDRESS_EDIT)

    useEffect(() => {
        updateFireplaces(globalVariable.sessionToken, setFireplaceIds)
        updateAddressCategories(globalVariable.sessionToken, setAddressCategories)
        updateAddressTypes(globalVariable.sessionToken, setAddressTypes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const address = newAddressForm.address.trim()
        const controller = new AbortController()

        if (address.length < 3) {
            setExistingAddresses([])
            setIsCheckingAddress(false)
            setAddressCheckFailed(false)
            return () => controller.abort()
        }

        setIsCheckingAddress(true)
        setAddressCheckFailed(false)
        const timeout = window.setTimeout(async () => {
            try {
                const response = await ky.post(API_URL + '/api/address/check-existing', {
                    json: { address },
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + globalVariable.sessionToken,
                        'Token': localStorage.getItem('token') as string
                    },
                    signal: controller.signal
                }).json<{ matches: IAddress[] }>()
                setExistingAddresses(response.matches)
            } catch {
                if (!controller.signal.aborted) {
                    setExistingAddresses([])
                    setAddressCheckFailed(true)
                }
            } finally {
                if (!controller.signal.aborted) setIsCheckingAddress(false)
            }
        }, 400)

        return () => {
            window.clearTimeout(timeout)
            controller.abort()
        }
    }, [globalVariable.sessionToken, newAddressForm.address])

    const openExistingAddress = (address: IAddress) => {
        setGlobalVariable(previous => ({
            ...previous,
            menuSelection: 'customers',
            addressToEdit: address
        }))
    }

    const createAddress = () => {
        ky.post(API_URL + '/api/address/create', {
            json: newAddressForm,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + globalVariable.sessionToken,
                'Token': localStorage.getItem('token') as string
            }
        }).then((data) => {
            if (data.status === 200) {
                toast.success('Uusi kohde lisätty')
                onAddComplete()
            }
        }).catch(() => {
            console.error('Kohteen lisääminen epäonnistui.')
            toast.error('Virhe kohteen lisäämisessä', { duration: 10000 })
        })
    }

    const onAddComplete = () => {
        setGlobalVariable({
            ...globalVariable,
            menuSelection: 'customers'
        })
    }

    return (
        <div className="customer-form-shell">
            <div className="customer-form-card">
                <div className="customer-form-header">
                    <h2 className="customer-form-title">Uusi kohde</h2>
                    <p className="mt-1 text-sm text-gray-500">Lisää asiakkaan yhteystiedot ja nuohouksen perustiedot.</p>
                </div>
                <form>
                    <div className="customer-form-body customer-form-grid">
                        <div>
                            <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-2">Etunimi</label>
                            <input
                                type="text"
                                id="firstname"
                                name="firstname"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, firstname: e.target.value })}
                                className=""
                            />
                        </div>
                        <div>
                            <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-2">Sukunimi</label>
                            <input
                                type="text"
                                id="lastname"
                                name="lastname"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, lastname: e.target.value })}
                                className=""
                            />
                        </div>
                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">Osoite</label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, address: e.target.value })}
                                className=""
                                autoComplete="street-address"
                                aria-describedby="existing-address-status"
                            />
                            <div id="existing-address-status" className="mt-2 min-h-6" aria-live="polite">
                                {isCheckingAddress && (
                                    <p className="flex items-center gap-2 text-sm text-gray-500">
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Tarkistetaan osoitetta…
                                    </p>
                                )}
                                {!isCheckingAddress && !addressCheckFailed && newAddressForm.address.trim().length >= 3 && existingAddresses.length === 0 && (
                                    <p className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                                        <FontAwesomeIcon icon={faCircleCheck} /> Osoitetta ei löytynyt nykyisistä kohteista.
                                    </p>
                                )}
                                {addressCheckFailed && (
                                    <p className="text-sm text-amber-700">Osoitteen tarkistus epäonnistui. Voit silti jatkaa lomakkeen täyttämistä.</p>
                                )}
                            </div>
                        </div>
                        {existingAddresses.length > 0 && (
                            <div className="sm:col-span-2 rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-sm" role="alert">
                                <div className="flex items-start gap-3">
                                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                                        <FontAwesomeIcon icon={faTriangleExclamation} />
                                    </span>
                                    <div>
                                        <h3 className="font-bold text-amber-950">Osoite löytyy jo</h3>
                                        <p className="mt-0.5 text-sm text-amber-800">Tarkista olemassa oleva kohde ennen uuden lisäämistä.</p>
                                    </div>
                                </div>
                                <div className="mt-3 grid gap-2">
                                    {existingAddresses.map(address => {
                                        const name = `${address.firstname || ''} ${address.lastname || ''}`.trim() || 'Nimetön'
                                        return (
                                            <div key={address._id.toString()} className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                                                <div className="min-w-0">
                                                    <p className="truncate font-bold text-slate-900">{name}</p>
                                                    <p className="mt-1 flex items-start gap-2 text-sm text-slate-600">
                                                        <FontAwesomeIcon icon={faLocationDot} className="mt-0.5 text-slate-400" />
                                                        <span>{address.address}{address.zip || address.city ? `, ${address.zip} ${address.city}`.trim() : ''}</span>
                                                    </p>
                                                </div>
                                                {canEditAddresses ? (
                                                    <button type="button" onClick={() => openExistingAddress(address)} className="flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
                                                        Avaa muokattavaksi <FontAwesomeIcon icon={faArrowRight} />
                                                    </button>
                                                ) : (
                                                    <p className="text-sm font-medium text-amber-800">Sinulla ei ole kohteen muokkausoikeutta.</p>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                        <div>
                            <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-2">Postinumero</label>
                            <input
                                type="text"
                                id="zip"
                                name="zip"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, zip: e.target.value })}
                                className=""
                            />
                        </div>
                        <div>
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">Paikkakunta</label>
                            <input
                                type="text"
                                id="city"
                                name="city"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, city: e.target.value })}
                                className=""
                            />
                        </div>
                        <div>
                            <label htmlFor="lastSweepDate" className="block text-sm font-medium text-gray-700 mb-2">Viimeisin nuohous</label>
                            <input
                                type="date"
                                id="lastSweepDate"
                                name="lastSweepDate"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, lastSweepDate: e.target.value })}
                                className=""
                            />
                        </div>
                        <div>
                            <label htmlFor="nextSweepingDate" className="block text-sm font-medium text-gray-700 mb-2">Seuraava nuohous</label>
                            <input
                                type="date"
                                id="nextSweepingDate"
                                name="nextSweepingDate"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, nextSweepDate: e.target.value })}
                                className=""
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Puhelinnumerot</label>
                            {newAddressForm.phoneNumbers.map((phoneNumber, index) => (
                                <div key={index} className="customer-phone-row">
                                    <input
                                        type="tel"
                                        inputMode="tel"
                                        pattern="^\+?[\d ]+$"
                                        value={phoneNumber}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            // Allow only one leading +, then digits
                                            value = value.replace(/(?!^)\+/g, '').replace(/[^0-9+]/g, '');
                                            if (value.startsWith('+')) {
                                                value = '+' + value.slice(1).replace(/\+/g, '');
                                            }
                                            const newPhoneNumbers = [...newAddressForm.phoneNumbers];
                                            newPhoneNumbers[index] = value;
                                            setNewAddressForm({ ...newAddressForm, phoneNumbers: newPhoneNumbers });
                                        }}
                                        className=""
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newPhoneNumbers = newAddressForm.phoneNumbers.filter((_, i) => i !== index)
                                            setNewAddressForm({ ...newAddressForm, phoneNumbers: newPhoneNumbers })
                                        }}
                                        className="flex min-h-12 items-center rounded-xl bg-red-50 px-3 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                                    >
                                        <span className='whitespace-nowrap'><FontAwesomeIcon icon={faTrash} /> Poista</span>
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    const newPhoneNumbers = [...newAddressForm.phoneNumbers, '']
                                    setNewAddressForm({ ...newAddressForm, phoneNumbers: newPhoneNumbers })
                                }}
                                className="customer-secondary-action mt-2 sm:w-auto"
                            >
                                <FontAwesomeIcon icon={faPlus} /> Lisää puhelinnumero
                            </button>
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">Lisätiedot</label>
                            <textarea
                                id="additionalInfo"
                                name="additionalInfo"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, additionalInfo: e.target.value })}
                                rows={4}
                                className=""
                            ></textarea>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Tulisijat</label>
                            <div className="customer-fireplace-grid">
                                {fireplaceIds.map((fireplace: IFireplace) => (
                                    <div key={fireplace._id} className="customer-fireplace-item">
                                        <input
                                            type="number"
                                            id={`fireplace-${fireplace.id}`}
                                            name="fireplaces"
                                            min="0"
                                            value={newAddressForm.selectedFireplaceIds.find(item => item.id === fireplace.id)?.count || 0}
                                            onChange={(e) => {
                                                const count = parseInt(e.target.value) || 0;
                                                const newSelectedFireplaceIds = newAddressForm.selectedFireplaceIds.filter(item => item.id !== fireplace.id);
                                                if (count > 0) {
                                                    newSelectedFireplaceIds.push({ id: fireplace.id, count });
                                                }
                                                setNewAddressForm({ ...newAddressForm, selectedFireplaceIds: newSelectedFireplaceIds });
                                            }}
                                            className=""
                                        />
                                        <label htmlFor={`fireplace-${fireplace._id}`} className="select-none">
                                            {fireplace.name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label htmlFor="addressType" className="block text-sm font-medium text-gray-700 mb-2">Osoitetyyppi</label>
                            <select
                                id="addressType"
                                name="addressType"
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, addressType: parseInt(e.target.value) })}
                                className=""
                            >
                                {addressTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="addressCategoryId" className="block text-sm font-medium text-gray-700 mb-2">Osoitekategoria</label>
                            <select
                                id="addressCategoryId"
                                name="addressCategoryId"
                                value={newAddressForm.addressCategoryId}
                                onChange={(e) => setNewAddressForm({ ...newAddressForm, addressCategoryId: parseInt(e.target.value) })}
                                className=""
                            >
                                {addressCategories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="customer-form-actions">
                        <button
                            onClick={createAddress}
                            type="button"
                            className="customer-primary-action"
                        >
                            Lisää
                        </button>
                        <button
                            type="button"
                            onClick={onAddComplete}
                            className="customer-danger-action"
                        >
                            Peruuta
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AddCustomer
